import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ISaveWorkoutStatePayload,
  ISessionWorkoutStats,
  ITrainingWorkoutState,
  IUpdateWorkoutComparisonPayload,
  IUpdateWorkoutStatsPayload,
  IWorkoutStats,
} from './types';

export interface ISessionState {
  loading: boolean;
  // current session domain id
  domain: number;
  // array of workout ids in current session
  session: number[];
  // array of completed flags for each workout in session
  completed: boolean[];
  // array of disabled (not supported) flags for each workout in session
  disabled: boolean[];
  // state of workouts in session
  sessionWorkouts: ISessionWorkoutStats;
  // workout state data saved to backend
  workoutStates: ITrainingWorkoutState[];
}

export const initialState: ISessionState = {
  loading: true,
  domain: 0,
  session: [],
  completed: [],
  disabled: [],
  sessionWorkouts: {},
  workoutStates: [],
};

export const statePropName = 'session';

const sessionSlice = createSlice({
  name: statePropName,
  initialState,
  reducers: {
    initSession(state, action: PayloadAction<number>) {
      return { ...initialState };
    },
    loadSession(state) {
      state.loading = true;
      state.domain = 0;
    },
    updateDomain(state, action: PayloadAction<number>) {
      state.domain = action.payload;
      state.session = [];
      state.completed = [];
    },
    updateSession(state, action: PayloadAction<number[]>) {
      state.session = action.payload;
    },
    updateCompleted(state, action: PayloadAction<boolean[]>) {
      state.completed = action.payload;
    },
    updateDisabled(state, action: PayloadAction<boolean[]>) {
      state.disabled = action.payload;
    },
    updateWorkoutStats: {
      reducer(state, action: PayloadAction<IUpdateWorkoutStatsPayload>) {
        const { workoutId, stats } = action.payload;
        const workoutState = state.sessionWorkouts[workoutId] || {};
        state.sessionWorkouts[workoutId] = { ...workoutState, ...stats };
      },
      prepare(workoutId: number, stats: Partial<IWorkoutStats>) {
        return { payload: { workoutId, stats } };
      },
    },
    updateWorkoutStates(state, action: PayloadAction<ITrainingWorkoutState[]>) {
      state.workoutStates = action.payload;
      state.loading = false;
    },
    openWorkout(state, action: PayloadAction<number>) {},
    saveWorkoutState: {
      reducer(state, action: PayloadAction<ISaveWorkoutStatePayload>) {},
      prepare(workoutId: number, goal: number, startDifficulty: number, reachDifficulty: number) {
        return { payload: { workoutId, goal, startDifficulty, reachDifficulty } };
      },
    },
    reloadSession(state) {
      state.loading = true;
      state.domain = 0;
    },
    updateWorkoutComparison: {
      reducer(state, action: PayloadAction<IUpdateWorkoutComparisonPayload>) {
        const { workoutId, comparison } = action.payload;
        const workoutState = state.sessionWorkouts[workoutId] || {};
        state.sessionWorkouts[workoutId] = { ...workoutState, comparison };
      },
      prepare(workoutId: number, comparison: number) {
        return { payload: { workoutId, comparison } };
      },
    },
  },
});

export const SessionActions = sessionSlice.actions;
export default sessionSlice.reducer;
